import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Text,
  Tooltip,
} from "@mantine/core"
import { useCallback, useState, useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"
import { PageHeader, PageLayout } from "../../../components/crud"
import type { Column } from "../../../components/DataTable"
import { DataTable, useDataTable } from "../../../components/DataTable"
import { useParams } from "react-router-dom"
import { useUser } from "../../user/hooks/useUser"
import type { AgencyUser } from "../models/agencyUser"
import { AgencyUserSchema, STATUS_MAP } from "../models/agencyUser"
import type { DrawerMode } from "./AgencyUserDrawer"
import AgencyUserDrawer from "./AgencyUserDrawer"

export default function AgencyUserList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencyUsers ?? false
  const canCreate = userData?.can?.createAgencyUser ?? false

  const {
    data,
    isLoading,
    page,
    pageSize,
    sort,
    sortDir,
    handleSortChange,
    handlePageChange,
  } = useDataTable<AgencyUser>({
    queryKey: "agency-users",
    url: "agency-user",
    schema: AgencyUserSchema,
    defaultSort: { field: "id", dir: "desc" },
    defaultPageSize: 50,
  })

  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const openDrawer = useCallback((id: number | null, mode: DrawerMode) => {
    setSelectedUserId(id)
    setDrawerMode(mode)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedUserId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => {
    closeDrawer()
  }, [closeDrawer])

  const handleSwitchToEdit = useCallback(() => {
    setDrawerMode("edit")
  }, [])

  const handleRowClick = useCallback(
    (v: AgencyUser) => {
      if (selectedUserId === v.id && drawerOpened) {
        closeDrawer()
      } else {
        openDrawer(v.id, "view")
      }
    },
    [openDrawer, closeDrawer, selectedUserId, drawerOpened],
  )

  const columns: Column<AgencyUser>[] = [
    {
      key: "username",
      label: t("agencyUsers.username"),
      sortable: true,
      render: (v) => (
        <Text fw={500} size="sm">
          {v.username}
        </Text>
      ),
    },
    {
      key: "full_name",
      label: t("agencyUsers.fullName"),
      sortable: true,
      render: (v) => <Text size="sm">{v.full_name}</Text>,
    },
    {
      key: "email",
      label: t("agencyUsers.email"),
      sortable: true,
      render: (v) => <Text size="sm">{v.email}</Text>,
    },
    {
      key: "status",
      label: t("agencyUsers.status"),
      sortable: true,
      width: 100,
      render: (v) => {
        const info = STATUS_MAP[v.status]
        return (
          <Badge color={info?.color ?? "gray"} variant="light" size="sm">
            {t(`agencyUsers.${info?.label ?? "inactive"}`)}
          </Badge>
        )
      },
    },
  ]

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("agencyUsers.title")}
          actions={
            <Group>
              {canCreate && (
                <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                  <Trans i18nKey="agencyUsers.create" />
                </Button>
              )}
            </Group>
          }
        />
      }
    >
      <DataTable<AgencyUser>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        sort={sort}
        sortDir={sortDir}
        loading={isLoading}
        emptyMessage={t("agencyUsers.empty")}
        keyExtractor={(v) => v.id}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        actions={(v) =>
          canManage ? (
            <Group gap={4} justify="flex-end" wrap="nowrap">
              <Tooltip label={t("common.edit")}>
                <ActionIcon
                  variant="subtle"
                  size="md"
                  onClick={() => openDrawer(v.id, "edit")}
                >
                  <i className="fa fa-pencil" />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : undefined
        }
      />

      <AgencyUserDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedUserId={selectedUserId}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
      />
    </PageLayout>
  )
}
